import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Star, ChevronRight } from 'lucide-react';
export function HeroSection() {
  return <section className="bg-warm-white py-2 md:py-24 px-3 md:px-4 relative overflow-hidden" data-id="element-110">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-111">
        <div className="absolute top-10 left-10 text-gold/20" data-id="element-112">
          <Star size={40} data-id="element-113" />
        </div>
        <div className="absolute top-40 right-20 text-sky/20" data-id="element-114">
          <Moon size={60} data-id="element-115" />
        </div>
        <div className="absolute bottom-20 left-1/4 text-lavender/30" data-id="element-116">
          <Star size={30} data-id="element-117" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-118">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="inline-flex items-center gap-2 bg-navy/5 text-navy px-3 py-1 md:px-4 md:py-2 rounded-full text-[11px] md:text-sm font-semibold mb-2 md:mb-8" data-id="element-119">
          <span className="text-gold" data-id="element-120">✨</span> Para madres y padres de bebés de
          4 a 24 meses
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-xl md:text-5xl lg:text-6xl font-bold text-navy leading-tight mb-1.5 md:mb-6" data-id="element-121">
          ¿Tu bebé no duerme bien y ya no sabés qué más hacer?
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="text-xs md:text-xl text-navy/80 mb-2 md:mb-8 max-w-3xl mx-auto leading-relaxed" data-id="element-122">
          Descubrí el sistema completo que ayuda a madres y padres agotados a
          entender por qué su bebé no descansa bien, corregir los errores que
          arruinan la noche y aplicar una rutina clara para empezar a ver
          cambios reales desde las primeras noches.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.25,
        duration: 0.5
      }} className="my-2 md:my-10 relative max-w-[160px] md:max-w-2xl mx-auto" data-id="element-123">
          <a href="#compra" className="block cursor-pointer group" data-id="element-124">
            <img src="https://cdn.magicpatterns.com/uploads/dZMqy72hZMEcKhCGojAMPB/31.png" alt="Método Noches de Paz - Libro Digital" className="relative w-full object-contain group-hover:-translate-y-1 transition-transform" data-id="element-125" />
          </a>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="text-sm md:text-2xl font-semibold text-navy mb-1.5 md:mb-6" data-id="element-126">
          Más sueño. Menos llanto. Menos confusión. Más paz para toda la casa.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="max-w-2xl mx-auto text-left space-y-1 md:space-y-4 mb-3 md:mb-10" data-id="element-127">
          <div className="flex items-start gap-1.5 md:gap-3" data-id="element-128">
            <span className="text-gold mt-0.5 text-[11px] md:text-base" data-id="element-129">✦</span>
            <p className="text-[11px] md:text-base text-navy/70 leading-snug" data-id="element-130">
              <strong className="text-navy" data-id="element-131">
                Entendé por qué no duerme bien
              </strong>{' '}
              — Detectá qué está afectando su sueño y dejá de probar cosas al
              azar.
            </p>
          </div>
          <div className="flex items-start gap-1.5 md:gap-3" data-id="element-132">
            <span className="text-gold mt-0.5 text-[11px] md:text-base" data-id="element-133">✦</span>
            <p className="text-[11px] md:text-base text-navy/70 leading-snug" data-id="element-134">
              <strong className="text-navy" data-id="element-135">
                Seguí una rutina clara paso a paso
              </strong>{' '}
              — Sabé qué hacer antes de dormirlo, al pasarlo a la cuna y si se
              despierta de noche.
            </p>
          </div>
          <div className="flex items-start gap-1.5 md:gap-3" data-id="element-136">
            <span className="text-gold mt-0.5 text-[11px] md:text-base" data-id="element-137">✦</span>
            <p className="text-[11px] md:text-base text-navy/70 leading-snug" data-id="element-138">
              <strong className="text-navy" data-id="element-139">
                Lográ noches más tranquilas sin culpa
              </strong>{' '}
              — Aplicá un método con presencia y claridad para que descanse
              mejor tu bebé y también vos.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="flex flex-col items-center" data-id="element-140">
          <a href="#compra" className="bg-navy hover:bg-navy-light text-white text-xs md:text-xl font-bold py-2.5 md:py-4 px-5 md:px-8 rounded-full shadow-soft transition-all transform hover:scale-105 flex items-center gap-2 w-full md:w-auto justify-center" data-id="element-141">
            👉 QUIERO ACCEDER AHORA AL MÉTODO NOCHES DE PAZ™
          </a>
          <p className="text-[10px] md:text-xs text-navy/60 mt-1.5 md:mt-4 font-medium" data-id="element-142">
            Acceso inmediato · Formato PDF · Lo recibís en minutos
          </p>
        </motion.div>
      </div>
    </section>;
}